<template lang="pug">
.slider-custom-wrapper
  .slider-custom
    .slider-wrapper(ref="sliderRef")
      Slider(v-model="val" :step="step" :min="min" :max="max" :disabled="disabled" :ariaLabel="ariaLabel")
      ul.range-labels(:class="disable ? 'disabled' : ''")
        li(
          v-for="label in rangeLabels.keys()"
          :key="label"
          @click="handleLabelClick(label)"
          :class="{ active: rangeLabels.get(label) === val, selected: rangeLabels.get(label) && rangeLabels.get(label) <= val }"
          class="label"
        )
          span {{ label }}
  .checkbox-wrapper(v-if="disabled != undefined")
    Checkbox(v-model="disabled" inputId="disabled" :binary="true")
    label(for="disabled") {{ disableText }}
</template>
<script lang="ts">
import {
  defineComponent,
  onMounted,
  ref,
  WritableComputedRef,
} from "vue";
import Slider from "primevue/slider";
import Checkbox from "primevue/checkbox";
import { computed, PropType } from "vue";

export default defineComponent({
  name: "SliderCustom",
  components: {
    Slider,
    Checkbox,
  },
  props: {
    modelValue: {
      type: Number,
      required: true,
    },
    step: {
      type: Number,
      required: true,
    },
    min: {
      type: Number,
      required: true,
    },
    max: {
      type: Number,
      required: true,
    },
    disable: {
      type: Boolean,
      required: false,
    },
    disableText: {
      type: String,
      required: false,
    },
    labels: {
      type: Array as PropType<string[]>,
      required: false,
    },
    ariaLabel: {
      type: String,
      required: false
    }
  },
  emits: ["update:disable", "update:modelValue", "select"],
  setup(props, { emit }) {
    const val: WritableComputedRef<number> = computed({
      get() {
        return props.modelValue;
      },
      set(value) {
        emit("update:modelValue", value);
      },
    });

    const disabled: WritableComputedRef<boolean> = computed({
      get() {
        return props.disable;
      },
      set(value) {
        emit("update:disable", value);
      },
    });

    // SLIDER
    const sliderRef = ref();
    const nArray = Array.from(
      { length: Math.floor((props.max - props.min) / props.step) + 1 },
      (_, index) => props.min + index * props.step
    );
    const rangeLabels: Map<string, number> = props.labels
      ? new Map(props.labels.map((key, i) => [key, nArray[i]]))
      : new Map(nArray.map((key) => [key.toString(), key]));

    const handleLabelClick = (label: string) => {
      if (!disabled.value) {
        emit("update:modelValue", rangeLabels.get(label));
      }
    };

    onMounted(() => {
      handleResize();
      window.addEventListener("resize", handleResize);
    });

    const handleResize = () => {
      const elements = sliderRef.value?.getElementsByClassName("label");
      if (elements) {
        Array.from(elements).forEach((element) => {
          const el = element as HTMLElement;
          el.style.width =
            sliderRef.value?.getElementsByClassName("p-slider")[0].clientWidth /
              (elements.length - 1) +
            "px";
        });
      }
    };

    return {
      handleLabelClick,
      sliderRef,
      disabled,
      val,
      rangeLabels,
    };
  },
});
</script>
<style scoped lang="scss">
.slider-wrapper {
  padding: 2rem 0;

  &:deep(.p-slider) {
    .p-slider-handle {
      z-index: 9;
    }
  }

  &:deep(.p-slider.p-disabled .p-slider-handle) {
    display: none;
  }
}

.checkbox-wrapper {
  padding: 2rem 0.25rem;
  display: flex;

  label {
    margin-left: 1rem;
    font-size: 1.1rem;
  }
}

.range-labels.range-labels {
  &.disabled {
    cursor: default;
    color: $background-color-disabled;

    li::before,
    li.selected::before {
      background-color: $background-color-disabled;
    }

    li.active::before {
      display: inline;
    }
  }

  &:nth-last-child(n + 5):nth-child(-n + 5) {
    left: -4rem;
    width: calc(100% + 8rem);
  }

  position: absolute;
  left: -1.5rem;
  display: flex;
  justify-content: center;
  margin-top: 18px;
  width: calc(100% + 3rem);
  padding: 0;
  list-style: none;

  li {
    position: relative;
    float: left;
    width: 100px;
    text-align: center;
    color: $form-slider-label-color;
    font-size: 0.875rem;
    cursor: pointer;

    &::before {
      position: absolute;
      top: -25px;
      right: 0;
      left: 0;
      content: "";
      margin: 0 auto;
      width: 9px;
      height: 9px;
      background: $background-color-disabled;
      border-radius: 50%;
    }

    &.active::before {
      display: none;
    }

    &.selected::before {
      background: $secondary_color;
    }

    @media screen and (max-width: 640px) {
      &:not(.active) span {
        display: none;
      }
    }
  }
}

.range-labels.range-labels:not(:nth-child(n + 6)):not(:nth-last-child(n + 6)) {
  left: -4.5rem;
  width: calc(100% + 9rem);
}

.slider-custom {
  padding: 0 1rem;
}
</style>
